import type { EntityType, Entity, Attribute, AttributeDataType, QueryFilter } from '../types';
import type { ColumnDef } from '../config/viewsConfig';
import { limsClient } from '../api/limsClient';

/** Max entities to list per JSON attribute when discovering keys (IS NOT NULL). */
export const JSON_DISCOVERY_LIMIT_PER_ATTR = 25;

/** Max entities to scan when inferring JSON key shapes. */
export const JSON_DISCOVERY_MAX_SAMPLE_ENTITIES = 25;

export interface DiscoveredJsonKey {
  key: string;
  path: string;
  attrName: string;
  hasValueSub: boolean;
}

export interface DiscoveredGroup {
  id: string;
  label: string;
  attrs: { name: string; data_type: AttributeDataType }[];
  jsonKeys?: DiscoveredJsonKey[];
}

/**
 * Sample up to N entities and discover top-level keys in JSON attributes.
 * Shared by ColumnPicker (for column discovery) and EntityFilters (for filter discovery).
 */
export function discoverJsonKeysFromEntities(
  entities: Entity[],
  jsonAttrNames: string[],
  entityType: EntityType,
  maxEntitySamples: number = JSON_DISCOVERY_MAX_SAMPLE_ENTITIES,
): { attrName: string; keys: DiscoveredJsonKey[] }[] {
  const sampled = entities.slice(0, maxEntitySamples);
  if (sampled.length === 0) return [];

  const result: { attrName: string; keys: DiscoveredJsonKey[] }[] = [];
  for (const attrName of jsonAttrNames) {
    const keyMap = new Map<string, any>();
    for (const entity of sampled) {
      const blob = entity.attributes?.[attrName];
      if (blob && typeof blob === 'object' && !Array.isArray(blob)) {
        for (const [k, v] of Object.entries(blob)) {
          if (!keyMap.has(k)) keyMap.set(k, v);
        }
      }
    }
    if (keyMap.size === 0) continue;

    const keys: DiscoveredJsonKey[] = [];
    const sortedKeys = Array.from(keyMap.entries()).sort(([a], [b]) => a.localeCompare(b));
    for (const [key, sampleValue] of sortedKeys) {
      const hasValueSub = sampleValue && typeof sampleValue === 'object' && 'value' in sampleValue;
      keys.push({
        key,
        path: hasValueSub ? `${key}.value` : key,
        attrName,
        hasValueSub,
      });
    }
    result.push({ attrName, keys });
  }
  return result;
}

/**
 * Build ColumnDef entries from discovered JSON keys.
 * Used by ColumnPicker.
 */
export function jsonKeysToColumnDefs(
  discovered: { attrName: string; keys: DiscoveredJsonKey[] }[],
  entityType: EntityType,
): { id: string; label: string; columns: ColumnDef[] }[] {
  return discovered.map(({ attrName, keys }) => ({
    id: `json_${attrName}`,
    label: `${attrName} (via ${entityType})`,
    columns: keys.map(k => ({
      key: `${attrName}_${k.key}`,
      label: `${attrName}.${k.path}`,
      extract: { from: attrName, path: k.path },
      sortable: false,
    })),
  }));
}

/**
 * Load direct + FK attributes for an entity type.
 * Shared by ColumnPicker and EntityFilters modals.
 */
export async function loadEntityAttributes(
  entityType: EntityType,
): Promise<{
  directAttrs: Attribute[];
  fkAttrs: Attribute[];
  jsonAttrNames: string[];
}> {
  const attrs = await limsClient.getAttributes(entityType);
  const directAttrs: Attribute[] = [];
  const jsonAttrNames: string[] = [];

  for (const a of attrs) {
    directAttrs.push(a);
    if (a.data_type === 'json') {
      jsonAttrNames.push(a.name);
    }
  }

  const fkAttrs = attrs.filter(a => a.referenced_entity_type);
  return { directAttrs, fkAttrs, jsonAttrNames };
}

/**
 * Load attributes for related entity types (via FK).
 * Returns one group per FK attribute, including JSON attribute names for discovery.
 */
export async function loadFkAttributeGroups(
  fkAttrs: Attribute[],
  cancelled: () => boolean,
): Promise<FkAttributeGroup[]> {
  const groups: FkAttributeGroup[] = [];

  await Promise.all(fkAttrs.map(async (fkAttr) => {
    const refType = fkAttr.referenced_entity_type!;
    try {
      const refAttrs = await limsClient.getAttributes(refType);
      if (cancelled()) return;
      const nonJsonAttrs = refAttrs.filter(ra => ra.data_type !== 'json');
      const jsonAttrNames = refAttrs.filter(ra => ra.data_type === 'json').map(ra => ra.name);
      groups.push({
        id: `fk_${fkAttr.name}`,
        label: `${refType.charAt(0).toUpperCase() + refType.slice(1)} (via ${fkAttr.name})`,
        fkAttrName: fkAttr.name,
        refType,
        attrs: nonJsonAttrs,
        jsonAttrNames,
      });
    } catch { /* skip */ }
  }));

  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
}

export type FkAttributeGroup = {
  id: string;
  label: string;
  fkAttrName: string;
  refType: EntityType;
  attrs: Attribute[];
  jsonAttrNames: string[];
  discoveredJson?: { attrName: string; keys: DiscoveredJsonKey[] }[];
};

export interface ModalAttributeContext {
  directAttrs: Attribute[];
  fkGroups: FkAttributeGroup[];
  jsonAttrNames: string[];
  discoveredJson: { attrName: string; keys: DiscoveredJsonKey[] }[];
}

/**
 * Paginated list for an entity type (used for JSON key discovery).
 */
export async function listEntitiesByType(
  entityType: EntityType,
  options: { limit?: number; filters?: QueryFilter[] } = {},
): Promise<Entity[]> {
  const { limit = 100, filters } = options;
  switch (entityType) {
    case 'sample': {
      const r = await limsClient.getSamples({ limit, filters });
      return r.data;
    }
    case 'test': {
      const r = await limsClient.getTests({ limit, filters });
      return r.data;
    }
    case 'assay': {
      const r = await limsClient.getAssays({ limit, filters });
      return r.data;
    }
    case 'order': {
      const r = await limsClient.getOrders({ limit, filters });
      return r.data;
    }
    default:
      return [];
  }
}

/**
 * Load entities that have each JSON attribute set, merge unique by id, or fall back to on-page entities.
 */
export async function fetchEntitiesForJsonDiscovery(
  entityType: EntityType,
  jsonAttrNames: string[],
  options: {
    extraFilters?: QueryFilter[];
    discoveryLimitPerAttr?: number;
    fallbackEntities?: Entity[];
    cancelled?: () => boolean;
  } = {},
): Promise<Entity[]> {
  const extraFilters = options.extraFilters ?? [];
  const discoveryLimitPerAttr = options.discoveryLimitPerAttr ?? JSON_DISCOVERY_LIMIT_PER_ATTR;
  const fallbackEntities = options.fallbackEntities ?? [];
  const cancelled = options.cancelled ?? (() => false);

  if (jsonAttrNames.length === 0) return [];

  let merged: Entity[] = [];
  try {
    const batches = await Promise.all(
      jsonAttrNames.map(async (attrName) => {
        if (cancelled()) return [] as Entity[];
        const filters: QueryFilter[] = [
          { attribute_name: attrName, operator: 'IS NOT NULL' },
          ...extraFilters,
        ];
        const data = await listEntitiesByType(entityType, {
          limit: discoveryLimitPerAttr,
          filters,
        });
        return cancelled() ? [] : data;
      }),
    );
    const byId = new Map<number, Entity>();
    for (const batch of batches) {
      for (const e of batch) {
        if (e.id != null && !byId.has(e.id)) byId.set(e.id, e);
      }
    }
    merged = Array.from(byId.values());
  } catch {
    merged = [];
  }

  if (merged.length === 0 && fallbackEntities.length > 0) {
    merged = fallbackEntities.slice(0, JSON_DISCOVERY_MAX_SAMPLE_ENTITIES);
  }
  return merged;
}

export interface LoadModalAttributeContextOptions {
  extraFilters?: QueryFilter[];
  fallbackEntities?: Entity[];
  discoveryLimitPerAttr?: number;
  cancelled?: () => boolean;
}

/**
 * Single entry point for filter modal + column picker: metadata, FK groups, and JSON key discovery.
 */
export async function loadModalAttributeContext(
  entityType: EntityType,
  options: LoadModalAttributeContextOptions = {},
): Promise<ModalAttributeContext> {
  const cancelled = options.cancelled ?? (() => false);
  const { directAttrs, fkAttrs, jsonAttrNames } = await loadEntityAttributes(entityType);
  const fkGroups = await loadFkAttributeGroups(fkAttrs, cancelled);

  const discoveryEntities = await fetchEntitiesForJsonDiscovery(entityType, jsonAttrNames, {
    extraFilters: options.extraFilters,
    discoveryLimitPerAttr: options.discoveryLimitPerAttr,
    fallbackEntities: options.fallbackEntities,
    cancelled,
  });

  const discoveredJson = discoverJsonKeysFromEntities(
    discoveryEntities,
    jsonAttrNames,
    entityType,
    JSON_DISCOVERY_MAX_SAMPLE_ENTITIES,
  );

  if (!cancelled()) {
    await Promise.all(fkGroups.map(async (g) => {
      if (g.jsonAttrNames.length === 0 || cancelled()) return;
      const fkEntities = await fetchEntitiesForJsonDiscovery(g.refType, g.jsonAttrNames, {
        discoveryLimitPerAttr: options.discoveryLimitPerAttr,
        cancelled,
      });
      if (cancelled()) return;
      g.discoveredJson = discoverJsonKeysFromEntities(
        fkEntities,
        g.jsonAttrNames,
        g.refType,
        JSON_DISCOVERY_MAX_SAMPLE_ENTITIES,
      );
    }));
  }

  return { directAttrs, fkGroups, jsonAttrNames, discoveredJson };
}
